<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import type { ScrollChapter } from '../composables/useInfiniteChapter'
import { getNextAdIndex, useGptAdManager } from '../composables/useGptAdManager'
import ScrollChapterUnlock from './ScrollChapterUnlock.vue'
import ScrollChapterAdGpt from './ScrollChapterAdGpt.vue'
import { createDebugger } from '@/utils/debug'
import { CHAPTER_LOCK } from '@/constants'
import { brand, brandConfig, features } from '@/config/brands'

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'pay-success'): void
  (e: 'reward-success'): void
}>()

const debug = createDebugger('ad')

interface Props {
  chapter: ScrollChapter
  contentId: string
  themeFont: {
    chapterTitle: string
    chapterContent: string
    paragraphSpacing: string
  }
  memberCoin: number
  isLastChapter?: boolean
  bookName?: string
  serialStatus?: number
}

const isTalewaveDivider = computed(() => brand === 'talewave')
const talewaveBookDividerIcon = computed(() => `url(/images/${brand}/chapter/unlock-divider-book.svg)`)

const paragraphs = computed(() => {
  if (!props.chapter.content)
    return []
  return props.chapter.content.split(/[\n]/).filter(Boolean)
})

const { SLOT_COUNT: adSlotCount } = useGptAdManager()

const paragraphItems = ref<Array<{ type: 'paragraph'; text: string } | { type: 'ad'; adSlotIndex: number; adInstanceId: string; adOrder: number }>>([])

watch(paragraphs, () => {
  const paras = paragraphs.value
  const threshold = Number(brandConfig.texts?.adSenseCharThreshold) || 500
  if (!features?.enableAdSense || paras.length === 0) {
    paragraphItems.value = paras.map(p => ({ type: 'paragraph' as const, text: p }))
    return
  }

  const items: Array<{ type: 'paragraph'; text: string } | { type: 'ad'; adSlotIndex: number; adInstanceId: string; adOrder: number }> = []
  let charCount = 0

  for (const para of paras) {
    items.push({ type: 'paragraph', text: para })
    charCount += para.length

    if (charCount >= threshold) {
      const adIdx = getNextAdIndex()
      items.push({
        type: 'ad',
        adSlotIndex: adIdx % adSlotCount,
        adInstanceId: `scroll-${adIdx}`,
        adOrder: adIdx + 1,
      })
      charCount = 0
    }
  }

  paragraphItems.value = items
}, { immediate: true })

const adItems = computed(() => paragraphItems.value.filter(item => item.type === 'ad'))

watch(
  () => [props.chapter.id, props.chapter.content],
  () => {
    if (!features?.enableAdSense)
      return
    const sequence = adItems.value.map(item => `#${item.adOrder}:detail${item.adSlotIndex + 1}`).join(', ') || 'none'
    debug('章节广告计划 mode=scroll chapterId=%s chapterNo=%s threshold=%s adCount=%s sequence=%s',
      props.chapter.id, props.chapter.sequenceNo, Number(brandConfig.texts?.adSenseCharThreshold) || 500, adItems.value.length, sequence)
  },
  { immediate: true },
)

const isLocked = computed(() => props.chapter.lockStatus === CHAPTER_LOCK.LOCK)
const dividerIcon = computed(() => `/images/${brand}/chapter/chapter-divider.png`)
</script>

<template>
  <div class="chapter-item px-20px py-10">
    <!-- 章节标题 -->
    <h2 :style="themeFont.chapterTitle" class="chapter-title font-800">
      {{ chapter.name }}
    </h2>

    <!-- 章节内容 -->
    <div :style="themeFont.chapterContent" class="chapter-content">
      <template v-for="(item, idx) in paragraphItems">
        <p v-if="item.type === 'paragraph'" :key="`p-${idx}`" :style="themeFont.paragraphSpacing">
          {{ item.text }}
        </p>
        <ScrollChapterAdGpt
          v-else
          :key="item.adInstanceId"
          :ad-slot-index="item.adSlotIndex"
          :ad-instance-id="item.adInstanceId"
          :ad-order="item.adOrder"
          :chapter-id="chapter.id"
        />
      </template>
    </div>

    <!-- 付费解锁区域（仅锁定章节显示） -->
    <ScrollChapterUnlock
      v-if="isLocked"
      :content-id="contentId"
      :chapter="chapter"
      :member-coin="memberCoin"
      :book-name="bookName"
      :serial-status="serialStatus"
      @pay-success="emit('pay-success')"
      @reward-success="emit('reward-success')"
    />

    <!-- 章节结束分隔符（最后一章不显示） -->
    <div v-if="!isLastChapter" class="chapter-divider">
      <span class="divider-line divider-line-left" :class="{ 'divider-line-talewave divider-line-talewave-left': isTalewaveDivider }" />
      <span v-if="isTalewaveDivider" class="divider-icon divider-icon-talewave" aria-hidden="true" />
      <img v-else :src="dividerIcon" class="divider-icon" alt="">
      <span class="divider-line divider-line-right" :class="{ 'divider-line-talewave divider-line-talewave-right': isTalewaveDivider }" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chapter-item {
  min-height: 50vh;
}

.chapter-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  gap: 12px;
  position: relative;
  top: 36px;
}

.divider-icon {
  width: 18px;
  flex-shrink: 0;
}

.divider-icon-talewave {
  width: 20px;
  height: 20px;
  display: inline-block;
  background-color: var(--unlock-divider-color, #A8AFBB);
  -webkit-mask-image: v-bind(talewaveBookDividerIcon);
  mask-image: v-bind(talewaveBookDividerIcon);
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.divider-line {
  flex: 1;
  max-width: 120px;
  height: 1px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
}

.divider-line-left {
  &::before {
    background: linear-gradient(to right, #AAB1B8, #9296A0);
    clip-path: polygon(0 25%, 100% 0, 100% 100%, 0 75%);
  }
}

.divider-line-right {
  &::before {
    background: linear-gradient(to right, #9296A0, #AAB1B8);
    clip-path: polygon(0 0, 100% 25%, 100% 75%, 0 100%);
  }
}

.divider-line-talewave {
  height: 1px;

  &::before {
    background: linear-gradient(to right, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
    clip-path: none;
  }
}

.divider-line-talewave-left {
  &::before {
    background: linear-gradient(to left, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
  }
}

.divider-line-talewave-right {
  &::before {
    background: linear-gradient(to right, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
  }
}
</style>
